import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";

import { useAuth } from "@/auth/useAuth";
import { UserAvatarMenu } from "@/components/navigation/UserAvatarMenu";

import "./Header.css";

export interface HeaderProps {
  /**
   * Optional slot for a notification bell. The dashboard has no notifications
   * endpoint yet, so the default is `undefined` — nothing renders.
   */
  readonly notificationBell?: ReactNode;
}

/**
 * Header for the admin surface. Modeled after the client marketing header,
 * trimmed to two menu items (Home + Login) and branded "Soft9Tech Admin".
 * Hub routes use HubSidebarLayout + HubTopBar instead of this component.
 */
export function Header({ notificationBell }: HeaderProps = {}): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { firebaseUser } = useAuth();

  useEffect(() => {
    const handleScroll = (): void => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return (): void => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const menuItems = [{ to: "/", label: "Home", exact: true }];

  return (
    <header
      className={`header ${scrolled ? "header--scrolled" : ""}`}
      role="banner"
    >
      <div className="container header__container">
        {/* Logo */}
        <Link to="/" className="header__brand" aria-label="Soft9Tech Admin Home">
          <img
            src="/images/logo.png"
            alt="Soft9Tech Admin"
            className="header__logo-img"
          />
        </Link>

        {/* Navigation */}
        <nav
          className="header__nav"
          role="navigation"
          aria-label="Main navigation"
        >
          <ul
            className={`header__menu ${mobileMenuOpen ? "header__menu--open" : ""}`}
          >
            {menuItems.map((item) => (
              <li key={item.to} className="header__menu-item">
                <NavLink
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }): string =>
                    `header__link${isActive ? " header__link--active" : ""}`
                  }
                  onClick={(): void => {
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right side controls */}
        <div className="header__controls">
          {firebaseUser ? (
            <>
              {notificationBell}
              <UserAvatarMenu />
            </>
          ) : (
            <Link to="/login" className="btn btn--primary btn--sm">
              Login
            </Link>
          )}

          {/* Mobile toggle */}
          <button
            type="button"
            className="header__mobile-toggle btn btn--ghost"
            onClick={(): void => {
              setMobileMenuOpen((prev) => !prev);
            }}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </header>
  );
}
